/**
 * Pont aria-hidden -> inert pour les overlays MODAUX Radix (DropdownMenu, Dialog,
 * AlertDialog, Select…).
 *
 * LE DÉFAUT MESURÉ (v030-menu-utilisateur-aria-hidden-focus-a-l-ouverture)
 * -------------------------------------------------------------------
 * Radix marque le reste de la page `aria-hidden="true"` à l'ouverture d'un menu/dialogue
 * MODAL, via le paquet `aria-hidden` (`hideOthers`, appelé par `@radix-ui/react-menu` et
 * les autres primitives modales). Ce paquet ne touche QUE l'attribut ARIA : il ne retire
 * jamais ces éléments de l'ordre de tabulation. Le focus clavier RÉEL reste donc atteignable
 * dans une zone annoncée invisible aux lecteurs d'écran — violation axe réelle
 * (aria-hidden-focus, serious), mesurée à l'ouverture du menu utilisateur (DropdownMenu) et
 * potentiellement partagée par tout autre overlay modal Radix du produit.
 *
 * LE CORRECTIF, SANS TOUCHER RADIX
 * ---------------------------------
 * L'attribut HTML natif `inert` fait ce que `aria-hidden` ne fait pas : il retire réellement
 * un sous-arbre de l'ordre de tabulation ET du survol. Les deux sont censés aller ensemble.
 * Ce module observe les changements de `aria-hidden` sur tout le document et synchronise
 * `inert` en miroir — jamais l'inverse : le comportement Radix (piège de focus DANS le
 * contenu ouvert, fermeture Échap, restitution du focus au déclencheur) n'est ni lu ni
 * modifié par ce pont, qui n'agit que sur les éléments DÉJÀ marqués aria-hidden="true" par
 * Radix lui-même.
 */
let arme = false

function synchroniser(el: Element): void {
  if (!(el instanceof HTMLElement)) return
  if (el.getAttribute('aria-hidden') === 'true') {
    el.setAttribute('inert', '')
  } else {
    el.removeAttribute('inert')
  }
}

/** Idempotent : n'installe l'observateur qu'une seule fois par document. */
export function armerPontAriaHiddenInert(): void {
  if (arme || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return
  arme = true

  const observateur = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
        synchroniser(mutation.target)
      }
    }
  })
  observateur.observe(document.body, {
    attributes: true,
    attributeFilter: ['aria-hidden'],
    subtree: true,
  })

  // État déjà présent au moment de l'armement (montage tardif, HMR).
  document.body.querySelectorAll('[aria-hidden="true"]').forEach(synchroniser)
}
